import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Play, 
  Pause, 
  CheckCircle2, 
  ArrowLeft,
  Info,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import api from '@/lib/api';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';

export function ApontamentoProducao() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [ordem, setOrdem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(false);

  useEffect(() => {
    if (id) carregarOrdem();
  }, [id]);

  const carregarOrdem = async () => {
    try {
      const res = await api.get(`/producao/${id}`);
      setOrdem(res.data);
    } catch (error) {
      toast({ title: 'Erro', description: 'Ordem não encontrada', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAcao = async (acao: 'iniciar' | 'pausar' | 'finalizar') => {
    try {
      setProcessando(true);
      
      let payload = {};
      if (acao === 'iniciar') payload = { operadorId: user?.id };
      if (acao === 'finalizar') {
        payload = { 
          quantidadeBoa: ordem.quantidadeSolicitada, 
          quantidadeRefugada: 0,
          tempoRealMinutos: 60, // Placeholder
          progressoPercentual: 100
        };
      }

      await api.post(`/producao/${id}/${acao}`, payload);
      
      toast({ title: 'Sucesso', description: `Produção ${acao}da com sucesso!` });
      carregarOrdem();
    } catch (error: any) {
      toast({ 
        title: 'Erro', 
        description: error.response?.data?.message || 'Falha na operação',
        variant: 'destructive' 
      });
    } finally {
      setProcessando(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-white">Carregando...</div>;
  if (!ordem) return <div className="p-8 text-center text-white">Ordem não encontrada.</div>;

  return (
    <div className="min-h-screen bg-fort3d-preto p-4">
      <div className="max-w-md mx-auto space-y-6">
        <button 
          onClick={() => navigate('/modo-fabrica')}
          className="flex items-center gap-2 text-fort3d-cinza-claro mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>

        <Card className="border-fort3d-laranja/30">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-fort3d-cinza-claro uppercase tracking-wider font-bold">Ordem de Produção</p>
                <CardTitle className="text-2xl text-white">{ordem.numeroOP}</CardTitle>
              </div>
              <Badge variant={ordem.status === 'PRODUCAO' ? 'success' : 'warning'}>
                {ordem.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="flex gap-4">
              <div className="w-20 h-20 rounded-lg bg-fort3d-cinza-escuro border border-fort3d-cinza-medio overflow-hidden">
                {ordem.produto.fotos?.[0] ? (
                  <img src={ordem.produto.fotos[0]} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-full h-full p-4 text-fort3d-cinza-claro" />
                )}
              </div>
              <div>
                <h3 className="text-white font-bold">{ordem.produto.nome}</h3>
                <p className="text-sm text-fort3d-cinza-claro">{ordem.produto.skuInterno}</p>
                <p className="text-emerald-500 font-bold text-lg">{ordem.quantidadeSolicitada} unidades</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 bg-fort3d-cinza-escuro/50 p-4 rounded-xl border border-fort3d-cinza-medio">
              <div>
                <p className="text-[10px] text-fort3d-cinza-claro uppercase">Máquina</p>
                <p className="text-sm text-white font-medium">{ordem.maquina.nome}</p>
              </div>
              <div>
                <p className="text-[10px] text-fort3d-cinza-claro uppercase">Material</p>
                <p className="text-sm text-white font-medium">PLA Premium</p>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-fort3d-cinza-medio">
              {ordem.status === 'NOVO' && (
                <Button 
                  className="w-full bg-fort3d-laranja hover:bg-fort3d-laranja/90 text-white h-16 text-lg font-bold"
                  leftIcon={<Play className="w-6 h-6" />}
                  onClick={() => handleAcao('iniciar')}
                  disabled={processando}
                >
                  INICIAR PRODUÇÃO
                </Button>
              )}

              {ordem.status === 'PRODUCAO' && (
                <>
                  <Button 
                    variant="outline"
                    className="w-full border-fort3d-laranja text-fort3d-laranja h-14"
                    leftIcon={<Pause className="w-5 h-5" />}
                    onClick={() => handleAcao('pausar')}
                    disabled={processando}
                  >
                    PAUSAR
                  </Button>
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-16 text-lg font-bold"
                    leftIcon={<CheckCircle2 className="w-6 h-6" />}
                    onClick={() => handleAcao('finalizar')}
                    disabled={processando}
                  >
                    FINALIZAR (CONCLUÍDO)
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info extra */}
        <div className="bg-fort3d-ciano/10 border border-fort3d-ciano/20 p-4 rounded-xl flex gap-3">
          <Info className="w-5 h-5 text-fort3d-ciano shrink-0" />
          <p className="text-xs text-fort3d-cinza-claro leading-relaxed">
            Certifique-se de que a mesa está limpa e o filamento carregado antes de iniciar. 
            Em caso de falha, use a aba "Qualidade" para reportar refugos.
          </p>
        </div>
      </div>
    </div>
  );
}
